import React, { useEffect } from 'react';
import './FilterNavigation.scss';
import LoadingSpinner from '../../../helpers/loadingSpinner';
import CustomHtmlTooltip from '../../../helpers/CustomHtmlTooltip';
import ButtonComponent from '../../ButtonComponent/ButtonComponent';
import './FilterNavButtons.scss';

interface FilterNavButtonsProps {
  onClearAll: () => void;
  onSearch: () => void;
  isDisabledSearch: boolean | undefined;
  isDisabledClear: boolean | undefined;
  onOpenAllFilters: () => void;
  onDownloadClick: () => void;
  isDownloadEnable: boolean | undefined;
  showSpinner: boolean | undefined;
  filterCount?: number;
}

interface DrawerFooterButtonsProps {
  onClearAll: () => void;
  onSearch: () => void;
  isDisabledSearch: boolean | undefined;
  isDisabledClear: boolean | undefined;
  footerClass?: string;
}

interface FilterButtonProps {
  label: string;
  count?: number;
  onClick?: () => void;
  className?: string;
}

const FilterCountButton: React.FC<FilterButtonProps> = ({
  label,
  count = 0,
  onClick,
  className = '',
}) => {
  return (
    <ButtonComponent
      variant="secondary"
      onClick={onClick}
      count={count}
      className={`${className} ${count > 0 ? 'has-count' : ''}`}
    >
      {label}
    </ButtonComponent>
  );
};

const FilterNavButtons: React.FC<FilterNavButtonsProps> & {
  DrawerFooterButtons: React.FC<DrawerFooterButtonsProps>;
} = ({ onClearAll, onSearch, isDisabledSearch, isDisabledClear, onOpenAllFilters, onDownloadClick, isDownloadEnable, showSpinner, filterCount }) => {

  return (
    <div className='filter-nav-btns' color="primary">
      <FilterCountButton
        label="All filters"
        count={filterCount}
        onClick={onOpenAllFilters}
        className="closed-filter-btn"
      />
      <FilterNavButtons.DrawerFooterButtons
        onClearAll={onClearAll}
        onSearch={onSearch}
        isDisabledSearch={isDisabledSearch}
        isDisabledClear={isDisabledClear}
      />
      {showSpinner ? (
        <div className='download-spinner' data-testid="download-button">
          <LoadingSpinner isLoading={showSpinner} width={29} height={55} />
        </div>

      ) : (
        <div className={`download-result ${(!isDisabledSearch && isDownloadEnable) ? "pointer-enabled" : ""}`}
          data-testid="download-button"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === ' ' && !isDisabledSearch && isDownloadEnable) {
              onDownloadClick();
            }
          }}
          onClick={() => {
            if (!isDisabledSearch && isDownloadEnable) {
              onDownloadClick();
            }
          }}>
          <CustomHtmlTooltip title={(!isDisabledSearch && isDownloadEnable) ? 'Export filtered results as CSV' : 'No data to export.'} placement="bottom" arrow>
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                <path d="M6 24.006V25.5C6 26.6935 6.47411 27.8381 7.31802 28.682C8.16193 29.5259 9.30653 30 10.5 30H25.5C26.6935 30 27.8381 29.5259 28.682 28.682C29.5259 27.8381 30 26.6935 30 25.5V24M18 6.75V23.25M18 23.25L23.25 18M18 23.25L12.75 18"
                  stroke={(!isDisabledSearch && isDownloadEnable) ? "#0074C8" : "#A6B3C0"}
                  strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </CustomHtmlTooltip>
        </div>
      )}
    </div>
  );
};

// DrawerFooterButtons: Only "Clear All" and "Search" buttons
const DrawerFooterButtons: React.FC<DrawerFooterButtonsProps> = ({
  onClearAll,
  onSearch,
  isDisabledSearch,
  isDisabledClear,
  footerClass = ''
}) => {
  const disableSearchCheck = () => isDisabledSearch ? true : null;
  const disableClearAllCheck = () => isDisabledClear ? true : null;

  return (
    <>
      <ButtonComponent
        variant="secondary"
        onClick={onClearAll}
        disabled={isDisabledClear}
        data-testid="clear-all"
        className="closed-filter-btn-clear"
      >
        Clear all
      </ButtonComponent>

      <ButtonComponent
        variant="primary"
        onClick={onSearch}
        disabled={disableSearchCheck()}
        data-testid="search"
        className={`closed-filter-btn${footerClass ? ' ' + footerClass + ' search' : '-base-search'}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 25 25" fill="none">
          <g>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.5488 3.04313C14.6909 3.03243 18.0574 6.38161 18.068 10.5237C18.0727 12.3246 17.4422 13.9789 16.3875 15.2745L20.872 19.7362C21.1657 20.0283 21.1669 20.5032 20.8747 20.7968C20.6092 21.0638 20.1926 21.0891 19.8984 20.872L19.8141 20.7996L15.3294 16.3381C14.0392 17.3994 12.3882 18.0384 10.5873 18.0431C6.44519 18.0538 3.07872 14.7046 3.06808 10.5625C3.05745 6.42035 6.40668 3.05383 10.5488 3.04313ZM10.5527 4.54312C7.23896 4.55168 4.55957 7.2449 4.56808 10.5586C4.57659 13.8723 7.26976 16.5516 10.5835 16.5431C13.8972 16.5345 16.5765 13.8413 16.568 10.5276C16.5595 7.21391 13.8664 4.53456 10.5527 4.54312Z"
              fill="currentColor"
            />
          </g>
        </svg>
      </ButtonComponent>

    </>
  );
};

FilterNavButtons.DrawerFooterButtons = DrawerFooterButtons;

export default FilterNavButtons;
